import { Modal } from 'antd';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type { BoardDiagnosis, DiagnosisSeverity } from '@/lib/boardDiagnosis';

const SEV: Record<
  DiagnosisSeverity,
  { color: string; bg: string; Icon: typeof Info }
> = {
  ok: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', Icon: CheckCircle2 },
  info: { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', Icon: Info },
  warn: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', Icon: AlertTriangle },
  error: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', Icon: XCircle },
};

export interface DiagnosisFact {
  label: string;
  value: string;
}

/** Click-to-diagnose popup: explains WHY a board reads offline / no-data and
 *  lists the concrete signals behind the verdict. Purely presentational — the
 *  caller computes `diagnosis` (diagnoseBoard) and `facts`. */
export function BoardDiagnosisModal({
  open,
  onClose,
  boardCode,
  diagnosis,
  facts,
}: {
  open: boolean;
  onClose: () => void;
  boardCode: string;
  diagnosis: BoardDiagnosis;
  facts: DiagnosisFact[];
}) {
  const sev = SEV[diagnosis.severity];
  const Icon = sev.Icon;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={540}
      title={`สถานะบอร์ด · ${boardCode}`}
    >
      {/* Headline banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          background: sev.bg,
          border: `1px solid ${sev.color}55`,
          borderRadius: 10,
          padding: '14px 16px',
          marginBottom: 16,
        }}
      >
        <Icon size={22} color={sev.color} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: sev.color }}>
            {diagnosis.headline}
          </div>
          <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 2 }}>
            สถานะ: {diagnosis.statusLabel}
          </div>
        </div>
      </div>

      {/* Reasons */}
      {diagnosis.reasons.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--dim)', marginBottom: 8 }}>
            รายละเอียด
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {diagnosis.reasons.map((r, i) => (
              <li key={i} style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)' }}>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Raw facts */}
      {facts.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '6px 14px',
            fontSize: 12.5,
            borderTop: '1px solid var(--border-color)',
            paddingTop: 12,
          }}
        >
          {facts.map((f) => (
            <div key={f.label} style={{ display: 'contents' }}>
              <div style={{ color: 'var(--dim)' }}>{f.label}</div>
              <div style={{ color: 'var(--text)', fontFamily: 'monospace' }}>{f.value}</div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
